import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {handleError} from "helpers"
import {generalSettingsService} from "services"
import {YesNo} from "types/common"
import {GeneralSetting, GeneralSettingField} from "types/settings/general-setting"

type SettingKey = "enableHighSchoolInAdmissionsModule" | "enablePartnerInAdmissionsModule"

type ExternalInstitutionViewSettings = Record<SettingKey, boolean>

const MAP_SETTING_KEY_2_GENERAL_SETTINGS_FIELD_NAME: Record<SettingKey, GeneralSettingField> = {
    enableHighSchoolInAdmissionsModule: GeneralSettingField.EnableHighSchoolInAdmissionsModule,
    enablePartnerInAdmissionsModule: GeneralSettingField.EnablePartnerInAdmissionsModule
}

const QUERY_KEY = "external-institution-view-settings"

const DEFAULT_SETTINGS: ExternalInstitutionViewSettings = {
    enableHighSchoolInAdmissionsModule: false,
    enablePartnerInAdmissionsModule: false
}

const parseSettingsInfo = (fieldSettings: GeneralSetting[]): ExternalInstitutionViewSettings =>
    Object.entries(MAP_SETTING_KEY_2_GENERAL_SETTINGS_FIELD_NAME).reduce(
        (acc, [settingKey, fieldName]) => ({
            ...acc,
            [settingKey]: fieldSettings.find((it) => it.field === fieldName)?.value === YesNo.Yes
        }),
        DEFAULT_SETTINGS
    )

export const useExternalInstitutionViewSettings = () => {
    const queryClient = useQueryClient()

    const updateSettingsM = useMutation<
        any,
        any,
        {field: SettingKey; checked: boolean},
        {previousSettings: ExternalInstitutionViewSettings}
    >(
        async ({field, checked}) => {
            const generalSettingsField = MAP_SETTING_KEY_2_GENERAL_SETTINGS_FIELD_NAME[field]

            await generalSettingsService.updateSettings([
                {field: generalSettingsField, value: checked ? YesNo.Yes : YesNo.No}
            ])
        },
        {
            onMutate: async ({field, checked}) => {
                await queryClient.cancelQueries({queryKey: [QUERY_KEY]})
                const previousSettings: ExternalInstitutionViewSettings = queryClient.getQueryData([QUERY_KEY])
                queryClient.setQueryData([QUERY_KEY], (old: ExternalInstitutionViewSettings) => ({
                    ...old,
                    [field]: checked
                }))

                return {previousSettings}
            },
            onError: (err, newSettings, context) => {
                queryClient.setQueryData([QUERY_KEY], context.previousSettings)
            },
            onSettled: () => {
                queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
            }
        }
    )

    const settingsQ = useQuery<ExternalInstitutionViewSettings>({
        queryKey: [QUERY_KEY],
        queryFn: async () => {
            const response = await generalSettingsService.getSettings([
                GeneralSettingField.EnableHighSchoolInAdmissionsModule,
                GeneralSettingField.EnablePartnerInAdmissionsModule
            ])

            return parseSettingsInfo(response)
        },
        initialData: DEFAULT_SETTINGS,
        onError: (err) => handleError(err)
    })

    return {
        settings: settingsQ.data,
        isLoadingSettings: settingsQ.isFetching,
        updateSettings: updateSettingsM.mutateAsync,
        isUpdatingSettings: updateSettingsM.isLoading
    }
}
